/**
 * Instantie-instellingen: wat per agency verschilt.
 *
 * NovaHub draait straks twee keer — Nova Temptation en Temptation Talent —
 * met dezelfde code maar een eigen database, eigen poort en eigen merknaam.
 * Die verschillen horen niet in de componenten thuis.
 *
 * De waarden komen uit `/config.json`, dat naast de build staat. Ontbreekt
 * dat bestand, dan gelden de standaardwaarden hieronder en gedraagt de app
 * zich exact zoals voorheen — een bestaande uitrol hoeft dus niets te doen.
 * Eén build, twee uitrollen, elk met een eigen `config.json`.
 */

export interface InstanceConfig {
  /** Naam boven in de zijbalk. */
  brand: string
  /** Regel eronder. */
  subtitle: string
  /** Twee letters in het logoblokje. */
  initials: string
  /** Basis voor API-aanroepen; leeg = dezelfde oorsprong als de pagina. */
  apiBase: string
  /** Waar de modelfoto's vandaan komen. */
  avatarBase: string
}

const STANDAARD: InstanceConfig = {
  brand: 'Nova Temptation',
  subtitle: 'Control Console',
  initials: 'AI',
  apiBase: '',
  avatarBase: '/avatars',
}

let huidig: InstanceConfig = { ...STANDAARD }

/** De instellingen van deze uitrol. */
export function instanceConfig(): InstanceConfig {
  return huidig
}

/**
 * Haalt `/config.json` op vóór de app rendert.
 *
 * Faalt bewust stil: een ontbrekend of stuk bestand mag nooit een leeg
 * dashboard opleveren. Dan gelden de standaardwaarden.
 */
export async function loadInstanceConfig(): Promise<InstanceConfig> {
  try {
    const r = await fetch('/config.json', { cache: 'no-store' })
    if (r.ok) {
      const raw = (await r.json()) as Partial<InstanceConfig>
      huidig = { ...STANDAARD, ...raw }
    }
  } catch {
    // standaardwaarden blijven staan
  }
  return huidig
}

/** Volledig pad naar een endpoint, met de basis van deze instantie ervoor. */
export function apiUrl(path: string): string {
  const basis = huidig.apiBase.replace(/\/$/, '')
  return basis ? `${basis}${path}` : path
}

/** Volledig pad naar een modelfoto. */
export function avatarUrl(src: string | null | undefined): string | undefined {
  if (!src) return undefined
  if (/^https?:\/\//.test(src)) return src
  // De backend levert "/avatars/x.jpg"; alleen het laatste stuk telt, de
  // basis komt van de instantie.
  const naam = src.replace(/^.*\/avatars\//, '').replace(/^\//, '')
  return `${huidig.avatarBase.replace(/\/$/, '')}/${naam}`
}
